import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<'contact' | 'careers'>('contact');
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [agreed, setAgreed] = useState(false);

  useSEO({
    title: 'Contact & Careers | Advantixx ACHC Accreditation Consultants',
    description: 'Contact Advantixx for ACHC accreditation consulting services or explore career opportunities. Read our strict no-solicitation policy before reaching out.',
    keywords: 'contact Advantixx, ACHC consulting contact, home health careers, accreditation consultant jobs',
    canonical: '/contact',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#webpage`,
      url: `${siteUrl}/contact`,
      name: 'Contact & Careers | Advantixx ACHC Accreditation Consultants',
      description: 'Contact Advantixx for ACHC accreditation consulting services or explore career opportunities with our team.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: {
        '@type': 'Organization',
        name: 'Advantixx Home Health Accreditation Consultants',
        url: siteUrl,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'info@advantixx.com',
          areaServed: 'US',
          availableLanguage: 'English',
        },
      },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('schema-contact')?.remove(); };
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) return;
    setFormStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name) data.append(input.name, input.value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7ddh0c5q56ra7tufjp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
        setAgreed(false);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
    <Navbar />
    <main className="pt-28 pb-24">
      {/* Page Header */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 text-center mb-14">
        <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">Get In Touch</p>
        <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">
          Contact &amp; Careers
        </h1>
        <p className="text-[#888] text-base max-w-2xl mx-auto leading-relaxed">
          Whether you&apos;re seeking ACHC accreditation consulting services or exploring career opportunities with Advantixx, please read all policies carefully before proceeding.
        </p>
      </section>

      {/* Tab Switcher */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
        <div className="inline-flex bg-[#1a1a1a] border border-[#d4af37]/20 rounded-full px-1 py-1">
          <button
            onClick={() => setActiveTab('contact')}
            className={`whitespace-nowrap px-8 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
              activeTab === 'contact'
                ? 'bg-[#d4af37] text-[#0d0d0d]'
                : 'text-[#888] hover:text-[#d4af37]'
            }`}
          >
            Contact Us
          </button>
          <button
            onClick={() => setActiveTab('careers')}
            className={`whitespace-nowrap px-8 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
              activeTab === 'careers'
                ? 'bg-[#d4af37] text-[#0d0d0d]'
                : 'text-[#888] hover:text-[#d4af37]'
            }`}
          >
            Careers
          </button>
        </div>
      </div>

      {/* CONTACT TAB */}
      {activeTab === 'contact' && (
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          {/* STRICT NO SOLICITATION POLICY BANNER */}
          <div className="border-2 border-red-600/70 bg-red-950/40 rounded-lg p-6 md:p-8 mb-10">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-fill text-red-500 text-2xl"></i>
              </div>
              <div>
                <h2 className="text-red-400 text-lg font-black uppercase tracking-widest mb-1">
                  Strict No-Solicitation Policy
                </h2>
                <p className="text-red-300/80 text-xs uppercase tracking-wider font-semibold">
                  Read carefully before submitting any message
                </p>
              </div>
            </div>

            <p className="text-[#ccc] text-sm leading-relaxed mb-5">
              This website and all associated businesses <strong className="text-white">strictly prohibit</strong> any unsolicited communications, including but not limited to sales, marketing, lead generation, promotional offers, partnerships, or outreach of any kind.
            </p>

            <div className="bg-[#0d0d0d]/60 border border-red-600/30 rounded-md p-5 mb-5">
              <p className="text-[#d4af37] text-xs uppercase tracking-widest font-bold mb-3">
                This contact form is exclusively reserved for:
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'Clients and patients actively seeking accreditation consulting services',
                  'Authorized referral sources with a verified, direct relationship',
                  'Verified business matters directly related to existing operations',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#bbb]">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#d4af37] text-sm"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-950/60 border border-red-600/40 rounded-md p-5 mb-5">
              <p className="text-red-400 text-xs uppercase tracking-widest font-bold mb-3">
                By submitting, you expressly acknowledge and agree that:
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  'Unsolicited sales, marketing, or promotional messages are strictly and absolutely prohibited.',
                  'Your information may be automatically filtered, blocked, or reported as spam without notice.',
                  'Repeated violations may result in reporting to relevant regulatory authorities, permanent blacklisting, and legal action where applicable.',
                  'We do not engage with cold outreach, vendor pitches, or unsolicited partnership requests under any circumstances.',
                  'Continued disregard of this policy constitutes intentional interference with business operations.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-red-200/80">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-close-circle-fill text-red-500 text-sm"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-red-300 text-sm font-bold text-center border-t border-red-600/30 pt-4">
              Any submission that does not meet the above criteria will be ignored, filtered, and permanently blocked without response.
            </p>
          </div>

          {/* Contact Form + Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Info */}
            <div className="lg:col-span-1 flex flex-col gap-6">
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Contact Information</p>
                <ul className="flex flex-col gap-5">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#d4af37]/10 rounded-md">
                      <i className="ri-mail-line text-[#d4af37] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[#555] text-xs mb-0.5">Email</p>
                      <p className="text-[#ccc] text-sm">info@advantixx.com</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#d4af37]/10 rounded-md">
                      <i className="ri-phone-line text-[#d4af37] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[#555] text-xs mb-0.5">Phone</p>
                      <p className="text-[#ccc] text-sm">+1 (800) 555-0199</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#d4af37]/10 rounded-md">
                      <i className="ri-map-pin-line text-[#d4af37] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[#555] text-xs mb-0.5">Service Area</p>
                      <p className="text-[#ccc] text-sm">United States — Nationwide</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-3">Response Time</p>
                <p className="text-[#888] text-sm leading-relaxed">
                  Legitimate inquiries are typically reviewed within <strong className="text-[#ccc]">2–3 business days</strong>. We do not guarantee responses to messages that do not meet our contact policy criteria.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {formStatus === 'success' ? (
                <div className="bg-[#141414] border border-[#d4af37]/20 rounded-lg p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto bg-[#d4af37]/10 rounded-full mb-5">
                    <i className="ri-check-line text-[#d4af37] text-2xl"></i>
                  </div>
                  <h3 className="text-white text-xl font-bold mb-3">Message Received</h3>
                  <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto">
                    If your inquiry meets our contact policy criteria, a member of our team will follow up within 2–3 business days.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="whitespace-nowrap mt-6 text-[#d4af37] text-sm font-semibold border border-[#d4af37]/40 px-6 py-2.5 rounded-full hover:bg-[#d4af37]/10 transition-colors duration-200 cursor-pointer"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleContactSubmit}
                  className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6 md:p-8 flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#888] text-xs uppercase tracking-wider font-semibold">Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="Jane Smith"
                        className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#888] text-xs uppercase tracking-wider font-semibold">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="jane@agency.com"
                        className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#888] text-xs uppercase tracking-wider font-semibold">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#888] text-xs uppercase tracking-wider font-semibold">Organization / Agency</label>
                      <input
                        type="text"
                        name="organization"
                        placeholder="Your Agency Name"
                        className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[#888] text-xs uppercase tracking-wider font-semibold">Reason for Contact *</label>
                    <select
                      name="reason"
                      required
                      className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] outline-none focus:border-[#d4af37]/50 transition-colors duration-200 cursor-pointer"
                    >
                      <option value="">Select a reason...</option>
                      <option value="ACHC Accreditation Consulting">ACHC Accreditation Consulting</option>
                      <option value="Mock Survey Preparation">Mock Survey Preparation</option>
                      <option value="Policy & Procedure Development">Policy &amp; Procedure Development</option>
                      <option value="Compliance Audit">Compliance Audit</option>
                      <option value="Staff Training">Staff Training</option>
                      <option value="Authorized Referral">Authorized Referral</option>
                      <option value="Existing Client Support">Existing Client Support</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[#888] text-xs uppercase tracking-wider font-semibold flex items-center justify-between">
                      <span>Message *</span>
                      <span className={`text-xs font-normal ${charCount > 480 ? 'text-red-400' : 'text-[#555]'}`}>{charCount}/500</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Describe your inquiry in detail..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-md px-4 py-3 text-sm text-[#ccc] placeholder-[#444] outline-none focus:border-[#d4af37]/50 transition-colors duration-200 resize-none"
                    />
                  </div>

                  {/* Policy Acknowledgment */}
                  <div className="bg-red-950/30 border border-red-600/30 rounded-md p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-0.5 flex-shrink-0 accent-[#d4af37] w-4 h-4 cursor-pointer"
                      />
                      <span className="text-xs text-[#aaa] leading-relaxed">
                        I confirm that my inquiry is legitimate and directly relevant to ACHC accreditation consulting services or an existing business relationship. I have read and agree to the <strong className="text-red-400">Strict No-Solicitation Policy</strong> above. I understand that unsolicited messages will be permanently blocked and may be reported.
                      </span>
                    </label>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={!agreed || formStatus === 'submitting'}
                    className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] text-sm font-bold px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Submit Inquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CAREERS TAB */}
      {activeTab === 'careers' && (
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          {/* STRICT FOLLOW-UP POLICY BANNER */}
          <div className="border-2 border-red-600/70 bg-red-950/40 rounded-lg p-6 md:p-8 mb-10">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-alarm-warning-fill text-red-500 text-2xl"></i>
              </div>
              <div>
                <h2 className="text-red-400 text-lg font-black uppercase tracking-widest mb-1">
                  Critical: Application Follow-Up Policy
                </h2>
                <p className="text-red-300/80 text-xs uppercase tracking-wider font-semibold">
                  Read before applying — violations result in immediate disqualification
                </p>
              </div>
            </div>

            <div className="bg-red-900/50 border border-red-500/50 rounded-md p-5 mb-5">
              <p className="text-white text-sm font-black uppercase tracking-wide text-center mb-3">
                ⚠ Calling or Following Up on Application Status Will Result in Permanent Application Cancellation ⚠
              </p>
              <p className="text-red-200 text-sm leading-relaxed text-center">
                This is a firm, non-negotiable policy with zero exceptions.
              </p>
            </div>

            <ul className="flex flex-col gap-3 mb-5">
              {[
                'Do NOT call our office to ask about your application status — ever.',
                'Do NOT email HR repeatedly to follow up on your application.',
                'Do NOT contact any staff member directly regarding employment inquiries.',
                'Do NOT send multiple applications for the same position.',
                'Any follow-up call, email, or contact regarding application status will result in immediate and permanent cancellation of your application — no exceptions, no appeals.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-red-200/90">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-fill text-red-500 text-sm"></i>
                  </div>
                  <strong>{item}</strong>
                </li>
              ))}
            </ul>

            <div className="bg-[#0d0d0d]/60 border border-[#d4af37]/20 rounded-md p-5">
              <p className="text-[#d4af37] text-xs uppercase tracking-widest font-bold mb-3">Our HR Process</p>
              <p className="text-[#bbb] text-sm leading-relaxed">
                Our HR team carefully reviews every application submitted. <strong className="text-white">If you are qualified and selected for consideration, HR will contact you directly.</strong> This process takes time and cannot be expedited by follow-up contact. Patience is required and expected. Applicants who respect this process demonstrate the professionalism we look for in our team.
              </p>
            </div>
          </div>

          {/* Careers Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Why Join */}
            <div className="lg:col-span-1 flex flex-col gap-6">
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Why Join Advantixx</p>
                <ul className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-award-line', text: 'Work with ACHC-certified industry leaders' },
                    { icon: 'ri-map-pin-line', text: 'Remote-friendly, nationwide opportunities' },
                    { icon: 'ri-book-open-line', text: 'Continuous professional development' },
                    { icon: 'ri-team-line', text: 'Mission-driven, compliance-focused culture' },
                    { icon: 'ri-shield-check-line', text: 'Integrity and excellence in everything we do' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-[#d4af37]/10 rounded-md">
                        <i className={`${item.icon} text-[#d4af37] text-sm`}></i>
                      </div>
                      <p className="text-[#888] text-sm leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-3">General Applications</p>
                <p className="text-[#888] text-sm leading-relaxed">
                  Don&apos;t see a role that fits? Submit a general application and we&apos;ll keep your information on file for future openings.
                </p>
              </div>
            </div>

            {/* Open Positions */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold">Current Openings</p>

              {[
                {
                  title: 'ACHC Accreditation Consultant',
                  type: 'Contract / Remote',
                  desc: 'Support home health agencies through the ACHC accreditation process. Must have direct ACHC survey experience and deep knowledge of home health compliance standards.',
                },
                {
                  title: 'Compliance Analyst',
                  type: 'Full-Time / Remote',
                  desc: 'Conduct compliance audits, review policies and procedures, and provide actionable recommendations to client agencies seeking or maintaining ACHC accreditation.',
                },
                {
                  title: 'Clinical Educator / Trainer',
                  type: 'Contract / Remote',
                  desc: 'Develop and deliver staff training programs aligned with ACHC standards. Experience in home health clinical operations required.',
                },
              ].map((job) => (
                <div key={job.title} className="bg-[#141414] border border-[#d4af37]/15 rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-white font-bold text-base mb-1">{job.title}</h3>
                      <span className="inline-block bg-[#d4af37]/10 text-[#d4af37] text-xs font-semibold px-3 py-1 rounded-full">
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <p className="text-[#888] text-sm leading-relaxed mb-4">{job.desc}</p>
                  <a
                    href="mailto:careers@advantixx.com?subject=Application: "
                    className="whitespace-nowrap inline-flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#d4af37]/10 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-mail-send-line text-sm"></i>
                    Apply via Email
                  </a>
                </div>
              ))}

              {/* Final reminder */}
              <div className="bg-red-950/30 border border-red-600/30 rounded-lg p-5 flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                </div>
                <p className="text-red-300 text-sm leading-relaxed">
                  <strong className="text-red-400 uppercase">Final Reminder:</strong> After submitting your application, do not call, email, or contact us to check on its status. HR will reach out if you are selected. Follow-up contact of any kind will result in the permanent cancellation of your application with no possibility of reinstatement.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
    <Footer />
    </div>
  );
}
